import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle, Loading, StatusChip } from "@/components/common";
import { Plus, X, Users } from "lucide-react";
import { toast } from "sonner";

export default function Crews() {
  const { user } = useAuth();
  const [crews, setCrews] = useState(null);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ crew_number: "", name: "", daily_target: 1960 });

  const load = async () => {
    const [c, u] = await Promise.all([api.get("/crews"), api.get("/users")]);
    setCrews(c.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.crew_number) { toast.error("Crew number required"); return; }
    try {
      await api.post("/crews", { ...form, daily_target: Number(form.daily_target) });
      toast.success(`${form.crew_number} created — naming preserved verbatim`);
      setOpen(false); setForm({ crew_number: "", name: "", daily_target: 1960 }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!crews) return <Loading label="Loading crews" />;
  const membersOf = (crew) => users.filter((u) => u.crew_id === crew.id);

  return (
    <div className="max-w-5xl">
      <SectionTitle sub="Naming convention preserved: Crew001, Crew0010, Crew00100 (never normalized)" right={
        user.role === "owner" && <button className="sg-btn sg-btn-accent" onClick={() => setOpen(true)} data-testid="new-crew-btn"><Plus size={18} /> New Crew</button>
      }>Crews</SectionTitle>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="crews-list">
        {crews.map((c) => (
          <div key={c.id} className="sg-card p-5" data-testid={`crew-${c.crew_number}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-head text-2xl font-extrabold">{c.crew_number}</span>
              <StatusChip status="ready" label={`$${c.daily_target.toLocaleString()}/day`} />
            </div>
            <div className="text-sm mb-3" style={{ color: "var(--txt2)" }}>{c.name}</div>
            <div className="flex items-center gap-2 text-sm"><Users size={16} style={{ color: "var(--accent)" }} />
              {membersOf(c).map((m) => m.name).join(", ") || "No members assigned"}
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setOpen(false)}>
          <div className="sg-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="new-crew-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-head text-2xl font-bold">New Crew</h3><button onClick={() => setOpen(false)}><X size={22} /></button></div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Crew number (verbatim, e.g. Crew0010)</label>
            <input className="sg-input mt-1 mb-3 font-mono" value={form.crew_number} data-testid="crew-number" onChange={(e) => setForm((f) => ({ ...f, crew_number: e.target.value }))} placeholder="Crew002" />
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Name</label>
            <input className="sg-input mt-1 mb-3" value={form.name} data-testid="crew-name" onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Daily target</label>
            <input className="sg-input mt-1 mb-4" type="number" value={form.daily_target} data-testid="crew-target" onChange={(e) => setForm((f) => ({ ...f, daily_target: e.target.value }))} />
            <button className="sg-btn sg-btn-accent w-full" onClick={create} data-testid="create-crew-submit">Create Crew</button>
          </div>
        </div>
      )}
    </div>
  );
}
